import { Portal } from '@chakra-ui/react';
import { Box } from '@components/ui/atoms/Box';
import { Flex } from '@components/ui/atoms/Flex';
import { HamburgerMenuIcon, Icon } from '@components/ui/atoms/Icons';
import { Text } from '@components/ui/atoms/Text';
import { useBreakpointValue } from '@shared/chakra/chakra';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MobileMenuContent } from './MobileMenuContent';

const MOBILE_MENU_TITLE_ID = 'main-header-mobile-menu-title';

/** Returns focusable elements within a container (links, buttons, [tabindex=0]) */
function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];

  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => el.offsetParent !== null && !el.hasAttribute('aria-hidden')
  );
}

export const MainHeader: React.FC = memo(() => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const menuTriggerRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const today = useMemo(() => {
    const date = new Date();
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    };

    return date.toLocaleDateString('en-US', options);
  }, []);

  // Desktop Header Styles
  const desktopHeaderStyles = useMemo(
    () => ({
      display: 'flex',
      w: 'full',
      padding: '16px 24px',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: '8px',
      background: 'body.50',
      boxShadow: '0 0 10px 0 rgba(236, 238, 241, 0.70)',
    }),
    []
  );

  // Mobile Header Styles
  const mobileHeaderStyles = useMemo(
    () => ({
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      w: 'full',
      p: '16px 12px',
      bg: 'body.50',
      borderRadius: '8px',
      boxShadow: '0 2px 10px 0 rgba(236, 238, 241, 0.70)',
      position: 'sticky' as const,
      top: '0',
      zIndex: '1000',
    }),
    []
  );

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (!isMobile || !isMenuOpen) return;

    const prev = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile, isMenuOpen]);

  // Focus management: trap focus in drawer when open, return focus to trigger when closed
  useEffect(() => {
    if (!isMobile || !isMenuOpen || !drawerRef.current) return;

    const drawer = drawerRef.current;
    const focusables = getFocusableElements(drawer);
    const first = focusables[0];

    if (first) {
      first.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || focusables.length === 0) return;

      const currentIndex = focusables.indexOf(document.activeElement as HTMLElement);

      if (e.shiftKey) {
        if (currentIndex <= 0) {
          e.preventDefault();
          focusables[focusables.length - 1]?.focus();
        }
      } else {
        if (currentIndex === -1 || currentIndex >= focusables.length - 1) {
          e.preventDefault();
          focusables[0]?.focus();
        }
      }
    };

    drawer.addEventListener('keydown', handleKeyDown);

    return () => {
      drawer.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobile, isMenuOpen]);

  const prevMenuOpenRef = useRef(isMenuOpen);

  useEffect(() => {
    if (!isMobile) return;

    const wasOpen = prevMenuOpenRef.current;

    prevMenuOpenRef.current = isMenuOpen;

    if (wasOpen && !isMenuOpen) {
      menuTriggerRef.current?.focus();
    }
  }, [isMobile, isMenuOpen]);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  // Render mobile layout
  if (isMobile) {
    return (
      <>
        <Flex {...mobileHeaderStyles}>
          <Flex gap="12px" alignItems="center">
            <Box
              ref={menuTriggerRef}
              cursor="pointer"
              p="2"
              borderRadius="8px"
              bg="transparent"
              display="flex"
              alignItems="center"
              justifyContent="center"
              tabIndex={0}
              role="button"
              onClick={() => {
                setIsMenuOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsMenuOpen(true);
                }
              }}
              aria-label="Open menu"
            >
              <Icon css={{ height: '20px', width: '20px' }}>
                <HamburgerMenuIcon />
              </Icon>
            </Box>
          </Flex>
          <Text width="250px" fontWeight="600" fontSize="14px" color="title.dark" lineHeight="150%">
            {today}
          </Text>

          <Flex alignItems="center" gap="16px" flexShrink="0">
            {/* TODO: Add notification icon when available from BE */}
          </Flex>
        </Flex>

        <Portal>
          <Box
            position="fixed"
            inset="0"
            zIndex="overlay"
            bg="blackAlpha.600"
            opacity={isMenuOpen ? 1 : 0}
            visibility={isMenuOpen ? 'visible' : 'hidden'}
            pointerEvents={isMenuOpen ? 'auto' : 'none'}
            transition="opacity 0.25s ease-out, visibility 0.25s ease-out"
            onClick={closeMenu}
            aria-hidden="true"
          />
          <Box
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={MOBILE_MENU_TITLE_ID}
            aria-hidden={!isMenuOpen}
            position="fixed"
            left="0"
            top="0"
            width="100vw"
            zIndex="modal"
            css={{
              height: '100vh',
              '@supports (height: 100dvh)': { height: '100dvh' },
              '::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
            bg="body.50"
            transform={isMenuOpen ? 'translateX(0)' : 'translateX(-100%)'}
            transition="transform 0.25s ease-out"
            overflow="hidden"
            display="flex"
            flexDirection="column"
          >
            <Box
              id={MOBILE_MENU_TITLE_ID}
              position="absolute"
              width="1px"
              height="1px"
              padding="0"
              margin="-1px"
              overflow="hidden"
              clip="rect(0, 0, 0, 0)"
              whiteSpace="nowrap"
              borderWidth="0"
            >
              Navigation menu
            </Box>
            <MobileMenuContent onClose={closeMenu} />
          </Box>
        </Portal>
      </>
    );
  }

  // Render desktop layout
  return (
    <Flex {...desktopHeaderStyles}>
      <Text width="250px" fontWeight="600" fontSize="14px" color="title.dark" lineHeight="150%">
        {today}
      </Text>
      <Flex width="250px" justifyContent="flex-end" alignItems="center" gap="24px" flexShrink="0">
        {/* <IconButton bg="inherit" borderRadius="8px" _hover={{ bg: 'brand.50' }}>
          <Icon css={{ width: '24px', height: '24px' }}>
            <ShopIcon />
          </Icon>
        </IconButton> */}
        {/* TODO: Add notification modal when available form BE */}
        {/* <NotificationModal /> */}
      </Flex>
    </Flex>
  );
});

MainHeader.displayName = 'MainHeader';
